"use client";

const ADMIN_SESSION_KEY = "webdifference.admin.session";

export interface AdminSession {
  email: string;
  name: string;
  timestamp: number;
}

// Liste des comptes admin définis dans les variables d'environnement
const ADMIN_ACCOUNTS: { email: string; password: string; name: string }[] = [
  {
    email: process.env.NEXT_PUBLIC_ADMIN_EMAIL_TRISTAN ?? "",
    password: process.env.NEXT_PUBLIC_ADMIN_PASSWORD_TRISTAN ?? "",
    name: "Tristan",
  },
  {
    email: process.env.NEXT_PUBLIC_ADMIN_EMAIL_FRED ?? "",
    password: process.env.NEXT_PUBLIC_ADMIN_PASSWORD_FRED ?? "",
    name: "Fred",
  },
  {
    email: process.env.NEXT_PUBLIC_ADMIN_EMAIL_RICHIE ?? "",
    password: process.env.NEXT_PUBLIC_ADMIN_PASSWORD_RICHIE ?? "",
    name: "Richie",
  },
];

/**
 * Vérifie les identifiants admin contre les comptes configurés
 * Retourne le nom du compte si trouvé, sinon null
 */
export function authenticateAdmin(email: string, password: string): string | null {
  const match = ADMIN_ACCOUNTS.find(
    (a) =>
      a.email &&
      a.password &&
      email.trim().toLowerCase() === a.email.trim().toLowerCase() &&
      password === a.password
  );
  return match?.name ?? null;
}

/**
 * Crée une session admin et la stocke dans localStorage
 */
export function createAdminSession(email: string, name: string): void {
  const session: AdminSession = {
    email: email.trim().toLowerCase(),
    name,
    timestamp: Date.now(),
  };
  localStorage.setItem(ADMIN_SESSION_KEY, JSON.stringify(session));
}

/**
 * Vérifie si une session admin valide existe
 */
export function isAdminAuthenticated(): boolean {
  if (typeof window === "undefined") return false;

  try {
    const stored = localStorage.getItem(ADMIN_SESSION_KEY);
    if (!stored) return false;

    const session: AdminSession = JSON.parse(stored);

    const maxAge = 30 * 24 * 60 * 60 * 1000; // 30 jours
    const knownEmails = ADMIN_ACCOUNTS
      .filter((a) => a.email)
      .map((a) => a.email.trim().toLowerCase());

    const isValid =
      knownEmails.includes(session.email) &&
      Date.now() - session.timestamp < maxAge;

    if (!isValid) {
      localStorage.removeItem(ADMIN_SESSION_KEY);
    }

    return isValid;
  } catch {
    return false;
  }
}

/**
 * Récupère l'email de la session admin actuelle
 */
export function getAdminEmail(): string | null {
  if (typeof window === "undefined") return null;

  try {
    const stored = localStorage.getItem(ADMIN_SESSION_KEY);
    if (!stored) return null;

    const session: AdminSession = JSON.parse(stored);
    return session.email;
  } catch {
    return null;
  }
}

/**
 * Récupère le nom du commercial connecté ("Tristan" ou "Fred")
 */
export function getAdminName(): string | null {
  if (typeof window === "undefined") return null;

  try {
    const stored = localStorage.getItem(ADMIN_SESSION_KEY);
    if (!stored) return null;

    const session: AdminSession = JSON.parse(stored);
    return session.name ?? null;
  } catch {
    return null;
  }
}

/**
 * Déconnecte l'admin (supprime la session)
 */
export function logoutAdmin(): void {
  if (typeof window === "undefined") return;
  localStorage.removeItem(ADMIN_SESSION_KEY);
}

/**
 * Hook pour écouter les changements d'authentification
 */
export function onAuthStateChange(
  callback: (isAuthenticated: boolean) => void
): () => void {
  if (typeof window === "undefined") return () => {};

  // Vérification initiale
  callback(isAdminAuthenticated());

  // Écoute des changements de localStorage (pour synchroniser entre onglets)
  const handleStorageChange = (e: StorageEvent) => {
    if (e.key === ADMIN_SESSION_KEY) {
      callback(isAdminAuthenticated());
    }
  };

  window.addEventListener("storage", handleStorageChange);

  // Polling pour détecter les changements dans le même onglet
  const interval = setInterval(() => {
    callback(isAdminAuthenticated());
  }, 1000);

  return () => {
    window.removeEventListener("storage", handleStorageChange);
    clearInterval(interval);
  };
}

